import { useCallback, useMemo } from 'react';

import { SearchSettings } from '@givebacks/api/query';

import { useCreateSearchObject } from '../../../src/hooks/router/searchParams';
import { TableSearchFieldProps } from '../types/searchFieldsTypes';
import { SearchProperties } from '../types/utils';

/**
 * Custom hook to create an initial search object based on provided search fields.
 *
 * @template T - A type that extends `SearchSettings`.
 * @param {SearchProperties<T['searches']>} fields - The search properties to be used for creating the initial search object.
 * @returns {string | undefined} - The initial value from the search object that matches the provided fields.
 *
 * @example
 * const initialSearchValue = useCreateInitialSearchObject(fields);
 *
 * @remarks
 * This hook utilizes `useCreateSearchObject` to get the current search object and maps the search fields to their corresponding values.
 * It then finds the first matching field from the provided fields and returns its value.
 *
 * @see {@link useCreateSearchObject}
 */
const useCreateInitialSearchObject = <T extends SearchSettings>(
  fields?: SearchProperties<T['searches']>
) => {
  const { searchObject: querySearchObject } = useCreateSearchObject();

  const getFieldMap = useMemo(() => {
    const search = querySearchObject?.search ?? {};
    const fieldMap = new Map<TableSearchFieldProps<any>['fields'][0], string>();
    Object.entries(search).forEach(([name, value]) => {
      // Don't want to add the 'by' key to the field map, this is part of the internal search object
      delete value['by'];
      fieldMap.set(name, Object.values(value)[0]);
    });
    return fieldMap;
  }, [querySearchObject?.search]);

  const getOrderValue = useCallback((): SearchSettings['order'] => {
    const order = querySearchObject?.order ?? {};
    const searchOrder = {
      field: '',
      direction: '',
    };
    // It is technically for order to be an array however we only support one order at a time
    // so we are going to use the last one
    Object.entries(order).forEach(([key, value]) => {
      searchOrder.field = key;
      searchOrder.direction = value;
    });
    if (!searchOrder.field) return;
    return searchOrder as SearchSettings['order'];
  }, [querySearchObject?.order]);

  const findMatchingPropKey = useCallback(
    <T extends SearchSettings>(fields: SearchProperties<T['searches']>) =>
      fields.find((field) => getFieldMap.has(field)) as unknown as keyof typeof getFieldMap,
    [getFieldMap]
  );

  const getInitialValue = useCallback(
    <T extends SearchSettings>(fields: SearchProperties<T['searches']>) => {
      const matchingPropKey = findMatchingPropKey(fields);

      return getFieldMap.get(matchingPropKey);
    },
    [findMatchingPropKey, getFieldMap]
  );

  const initialValue = useMemo(() => {
    if (!fields) return;
    const matchingPropKey = findMatchingPropKey(fields);
    return getFieldMap.get(matchingPropKey);
  }, [fields, findMatchingPropKey, getFieldMap]);

  return { initialValue, getInitialValue, getOrderValue };
};

export { useCreateInitialSearchObject };
